import { useEffect, useMemo, useRef, useState } from 'react'
import { useThemeMode } from '@/contexts/ThemeContext'
import { processHtmlForTheme } from '@/lib/htmlProcessor'

interface ArtifactHtmlViewerProps {
  content: string
  onFocus?: () => void
}

/**
 * Renders user-provided HTML inside a sandboxed iframe.
 *
 * Security note: `allow-same-origin` is intentionally omitted. Combining
 * `allow-scripts` with `allow-same-origin` would let untrusted HTML execute
 * script with same-origin privileges, which can enable data access and
 * parent-page interaction. If you ever need same-origin access for trusted
 * content, serve it from a separate origin instead of relaxing this sandbox.
 */
export default function ArtifactHtmlViewer({
  content,
  onFocus,
}: ArtifactHtmlViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const blobUrlRef = useRef<string | null>(null)
  const lastContentRef = useRef<string | null>(null)
  const [iframeSrc, setIframeSrc] = useState<string | null>(null)
  const { isDark, colors } = useThemeMode()

  // Process HTML with theme styling
  const processedContent = useMemo(() => {
    return processHtmlForTheme(content, {
      fontFamily:
        'var(--font-departure-mono), "Noto Emoji", "IBM Plex Mono", monospace',
      backgroundColor: colors.primaryBg,
      textColor: colors.primaryText,
      isDark,
    })
  }, [content, colors.primaryBg, colors.primaryText, isDark])

  // Create blob URL only when content actually changes
  // Blob URLs are more stable than srcDoc - browsers treat them as real documents
  useEffect(() => {
    // Skip if content hasn't changed
    if (lastContentRef.current === processedContent) return
    lastContentRef.current = processedContent

    // Revoke old blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
    }

    // Create new blob URL
    const blob = new Blob([processedContent], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    blobUrlRef.current = url
    setIframeSrc(url)

    return () => {
      // Cleanup on unmount
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [processedContent])

  useEffect(() => {
    if (!onFocus) return

    const iframe = iframeRef.current
    if (!iframe) return

    // Track if we've already called onFocus to avoid excessive calls
    let hasFocus = false

    // Check if iframe has focus every 200ms (reduced frequency to avoid interference)
    // This is necessary because iframe clicks don't propagate to parent
    const interval = setInterval(() => {
      const activeElement = document.activeElement
      const isFocused = activeElement === iframe

      // Only call onFocus when focus state changes
      if (isFocused && !hasFocus) {
        hasFocus = true
        onFocus()
      } else if (!isFocused && hasFocus) {
        hasFocus = false
      }
    }, 200)

    return () => clearInterval(interval)
  }, [onFocus])

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {iframeSrc && (
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          sandbox="allow-scripts allow-forms allow-modals allow-popups"
          allow="clipboard-read; clipboard-write; camera; microphone; fullscreen"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: colors.primaryBg,
          }}
          title="HTML Artifact"
        />
      )}
    </div>
  )
}
